import React, { ReactElement, useRef } from 'react'
import { AnimatePresence } from 'framer-motion'
import { Box, Theme } from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import { HistoryLocation } from '@gatsbyjs/reach-router'

import { pageTransition, StyledMotionMain } from '@utils/motion'
import useStrapiNavigation from '@components/site/hooks/contentful/useStrapiNavigation'
import Header from '@components/site/container/Header/'
import Footer from '@components/site/container/Footer/'
import useGlobalState from '@components/site/hooks/useGlobalState'
import PortalProvider from '@components/site/providers/PortalProvider'

export type LayoutProps = MCDC.Props.IDefault & {
  location: HistoryLocation
  pageContext?: MCDC.Contentful.IPageContext
}

export default function Layout({
  children,
  location,
  pageContext,
}: LayoutProps): ReactElement {
  const globalState = useGlobalState()
  const footerPortalRef = useRef<HTMLDivElement>(null)
  const { homePage, mainPages, metaPages, accountPage } = useStrapiNavigation()

  const navFallback: MCDC.Props.ILinkTo = {
    label: '',
    url: '/',
    isExternal: false,
  }

  const navItems = [
    homePage?.url ? homePage : navFallback,
    ...(mainPages || []),
    accountPage?.url ? accountPage : navFallback,
  ]

  return (
    <>
      <Header
        navItems={navItems}
        location={location}
      />
      <PortalProvider portalRef={footerPortalRef} location={location}>
        <AnimatePresence
          exitBeforeEnter
          onExitComplete={() => globalState.setLocation(location)}
        >
          <StyledMotionMain
            id="main"
            tabIndex={-1}
            key={location.pathname}
            {...pageTransition}
            sx={{
              position: 'relative',
              minHeight: '80vh',
              overflow: 'hidden',
              pt: 16,
            }}
          >
            <Box
              sx={(muiTheme: Theme) => ({
                ...muiTheme.mixins.conatainerModuleSpacing('large'),
                pb: 10,
              })}
            >
              {pageContext?.page?.metaTags?.metaTitle && (
                <Box
                  component="h1"
                  sx={visuallyHidden}
                  className="visually-hidden"
                >
                  {pageContext.page.metaTags.metaTitle}
                </Box>
              )}

              {children}
            </Box>
            <Box
              sx={{
                position: 'fixed',
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 100,
                pointerEvents: 'none',
              }}
            >
              <div ref={footerPortalRef} />
            </Box>
          </StyledMotionMain>
        </AnimatePresence>
      </PortalProvider>

      <Footer navItems={metaPages} />
    </>
  )
}
